import {
  getCriticality,
  getPrismPriorityRank,
  isFullPrismRecord,
  parseDateOnly,
} from "./prismSubscriptionUtils";
import dayjs from "dayjs";

export const CALENDAR_EVENT_TYPES = {
  renewal: {
    label: "Renewal",
    shortLabel: "Renewal",
  },
  end: {
    label: "Contract End",
    shortLabel: "End",
  },
};

export const DEFAULT_NOTE_COLOR = "purple";

export const NOTE_COLOR_OPTIONS = [
  { value: "blue", label: "Blue" },
  { value: "green", label: "Green" },
  { value: "amber", label: "Amber" },
  { value: "red", label: "Red" },
  { value: "purple", label: "Purple" },
  { value: "gray", label: "Gray" },
];

const NOTE_COLOR_VALUES = new Set(
  NOTE_COLOR_OPTIONS.map(({ value }) => value),
);

export const normalizeNoteColor = (value) =>
  NOTE_COLOR_VALUES.has(value) ? value : DEFAULT_NOTE_COLOR;

export const getNoteColorLabel = (value) => {
  const normalizedColor = normalizeNoteColor(value);

  return (
    NOTE_COLOR_OPTIONS.find(
      ({ value: optionValue }) => optionValue === normalizedColor,
    )?.label || "Purple"
  );
};

export const getNoteColorClassName = (value) =>
  `prism-calendar-note-color--${normalizeNoteColor(value)}`;

export const getCalendarDateKey = (value) => {
  const date = parseDateOnly(value);

  if (!date) return null;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const getCalendarEventTypeLabel = (types = [], short = false) => {
  if (types.length > 1) {
    return short ? "Renewal / End" : "Renewal / Contract End";
  }

  const eventType = CALENDAR_EVENT_TYPES[types[0]];
  return short ? eventType?.shortLabel || "Event" : eventType?.label || "Event";
};

export const getCalendarEventTypeClass = (types = []) => {
  if (types.length > 1) return "combined";
  return types[0] || "event";
};

export const sortCalendarEvents = (events = []) =>
  [...events].sort((a, b) => {
    const priorityA = getPrismPriorityRank({
      criticality: a.criticality,
      contract_status: a.subscription?.contract_status,
    });
    const priorityB = getPrismPriorityRank({
      criticality: b.criticality,
      contract_status: b.subscription?.contract_status,
    });

    if (priorityA !== priorityB) return priorityA - priorityB;

    return String(a.subscription?.subscription_name || "").localeCompare(
      String(b.subscription?.subscription_name || ""),
    );
  });

export const buildCalendarEvents = (
  subscriptions = [],
  details = {},
  eventTypeFilter = null,
) => {
  const events = [];

  subscriptions.forEach((subscription) => {
    const isFullRecord = isFullPrismRecord(subscription, details);
    if (!isFullRecord) return;

    const criticality = getCriticality(
      subscription.contract_renewal_date,
      subscription.subscription_type,
      subscription.contract_status,
    );
    const renewalDateKey = getCalendarDateKey(
      subscription.contract_renewal_date,
    );
    const endDateKey = getCalendarDateKey(subscription.contract_end_date);

    const addEvent = (dateKey, types) => {
      if (!dateKey) return;
      if (eventTypeFilter && !types.includes(eventTypeFilter)) return;

      events.push({
        key: `${subscription.id}-${types.join("-")}-${dateKey}`,
        dateKey,
        types,
        criticality,
        isFullRecord,
        subscription,
      });
    };

    if (renewalDateKey && renewalDateKey === endDateKey) {
      addEvent(renewalDateKey, ["renewal", "end"]);
    } else {
      addEvent(renewalDateKey, ["renewal"]);
      addEvent(endDateKey, ["end"]);
    }
  });

  return events;
};

export const groupCalendarEventsByDate = (events = []) =>
  events.reduce((eventsByDate, event) => {
    if (!eventsByDate[event.dateKey]) eventsByDate[event.dateKey] = [];
    eventsByDate[event.dateKey].push(event);
    return eventsByDate;
  }, {});

export const getCalendarNoteDateKey = (note) => note?.note_date || null;

const getCalendarNoteCreatedTime = (note) => {
  const createdTime = Date.parse(note?.created_at || "");
  return Number.isNaN(createdTime) ? 0 : createdTime;
};

export const sortCalendarNotes = (notes = []) =>
  [...notes].sort((a, b) => {
    const dateCompare = String(a.note_date || "").localeCompare(
      String(b.note_date || ""),
    );

    if (dateCompare !== 0) return dateCompare;

    const createdCompare =
      getCalendarNoteCreatedTime(b) - getCalendarNoteCreatedTime(a);

    if (createdCompare !== 0) return createdCompare;

    return Number(b.id || 0) - Number(a.id || 0);
  });

export const groupCalendarNotesByDate = (notes = []) =>
  sortCalendarNotes(notes).reduce((notesByDate, note) => {
    const dateKey = getCalendarNoteDateKey(note);

    if (!dateKey) return notesByDate;

    if (!notesByDate[dateKey]) notesByDate[dateKey] = [];
    notesByDate[dateKey].push(note);

    return notesByDate;
  }, {});

export const buildCalendarDateItems = (notes = [], events = []) => [
  ...events.map((event) => ({
    kind: "event",
    key: event.key,
    event,
  })),
  ...sortCalendarNotes(notes).map((note) => ({
    kind: "note",
    key: `note-${note.id}`,
    note,
  })),
];

export const setCalendarYear = (date, selectedYear) => {
  if (!date || !selectedYear) return date;

  const year = dayjs.isDayjs(selectedYear)
    ? selectedYear.year()
    : Number(selectedYear);

  return Number.isInteger(year) ? dayjs(date).year(year) : date;
};

export const setCalendarMonthAndYear = (date, selectedMonth) => {
  const currentDate = dayjs(date);
  const targetMonth = dayjs(selectedMonth);

  if (!currentDate.isValid() || !targetMonth.isValid()) return date;

  const dayOfMonth = Math.min(
    currentDate.date(),
    targetMonth.daysInMonth(),
  );

  return currentDate
    .date(1)
    .year(targetMonth.year())
    .month(targetMonth.month())
    .date(dayOfMonth);
};

export const getCalendarWeekCount = (date) => {
  const month = dayjs(date);

  if (!month.isValid()) return 6;

  const occupiedCalendarCells =
    month.startOf("month").day() + month.daysInMonth();

  return Math.min(6, Math.max(4, Math.ceil(occupiedCalendarCells / 7)));
};

export const getCalendarDateCellSummary = (
  notes = [],
  events = [],
  visibleItemLimit = 3,
) => {
  const items = buildCalendarDateItems(notes, events);

  return {
    items,
    visibleItems: items.slice(0, visibleItemLimit),
    hiddenItemCount: Math.max(items.length - visibleItemLimit, 0),
  };
};
